import type { AxiosError } from 'axios';
import axios from 'axios';

import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import type { StatusKey } from '../../../enum';
import { Client } from '../../../models/client';
import type { ErrorMessage } from '../../../models/error';
import type { AsyncThunkConfig } from '../../../models/slice';
import type { FilteredInfo, RequestParams, TableChange } from '../../../models/table';
import { SortedInfo } from '../../../models/table';
import { RaygunErrorHandlerService } from '../../../service/raygun.service';
import { removeEmpty } from '../../../service/table.service';
import { sentiment_status } from './dashboard.helper';

const { logError } = RaygunErrorHandlerService();

export const Priorities = {
  Immediate1: '1',
  Emergent2: '2',
  Urgent3: '3',
  LessUrgent4: '4',
  NonUrgent5: '5',
} as const;
export type Priorities = (typeof Priorities)[keyof typeof Priorities];

export const RiskStatus = {
  Critical: 'critical_risk',
  High: 'high_risk',
  Medium: 'medium_risk',
  Low: 'low_risk',
  Inactive: 'inactive',
  NotJournaledYet: null,
} as const;

export type RiskStatus = (typeof RiskStatus)[keyof typeof RiskStatus];

export class DashboardFilterButton {
  current: number = 0;
  label: string = '';
  subLabel: string = '';
  backgroundColour: string = '';
  textColour: string = '';
  type: 'priority_number' | 'risk' | 'case_status_group' | null = null;
  filterBy: RiskStatus | Priorities | null = null;
  risk_status: StatusKey | null = null;
}

type DashboardSliceType = {
  clients: Client[];
  requestClientParam: RequestParams<Client>;
  loadingClients: boolean;
  client: Client;
};

const initialState: DashboardSliceType = {
  clients: [],
  requestClientParam: {
    pagination: {
      current: 1,
      pageSize: 50,
      defaultPageSize: 50,
    },
    sortedInfo: new SortedInfo<Client>('last_check_in', 'descend'),
    filteredInfo: { my_client: ['true', 'false'] },
  },
  loadingClients: false,
  client: new Client(),
};

export const fetchClients = createAsyncThunk<Client[], undefined, AsyncThunkConfig>(
  'dashboard/fetchClients',
  async (_, thunkAPI) => {
    try {
      const requestClientParam = thunkAPI.getState().dashboardSlice.requestClientParam;
      const response = (await axios.post('v2_client', requestClientParam, {
        signal: thunkAPI.signal,
      })) as Client[];
      return response ?? [];
    } catch (e) {
      logError(e, ['dashboardSlice', 'fetchClients']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    handleTableChange: (state, action: PayloadAction<TableChange<Client>>) => {
      const filters = action.payload.filters as FilteredInfo;
      const sorter = action.payload.sorter as SortedInfo<Client>;
      if (!sorter.order) {
        sorter.order = null;
      }
      state.requestClientParam.pagination = action.payload.pagination;
      state.requestClientParam.sortedInfo = sorter;
      state.requestClientParam.filteredInfo = removeEmpty({ ...state.requestClientParam.filteredInfo, ...filters });
    },
    handleFiltersChange: (state, action: PayloadAction<FilteredInfo>) => {
      const filters = action.payload;
      state.requestClientParam.filteredInfo = removeEmpty(filters);
    },
    clearTableFilters: (state) => {
      state.requestClientParam = {
        ...initialState.requestClientParam,
        filteredInfo: { my_client: state.requestClientParam.filteredInfo.my_client ?? [] },
      };
    },
    clearCaseStatusFilters: (state) => {
      const {
        case_status_id: _case_status_id,
        case_status_group: _case_status_group,
        ...rest
      } = state.requestClientParam.filteredInfo;
      state.requestClientParam.filteredInfo = rest;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClients.pending, (state, _action) => {
        state.loadingClients = true;
      })
      .addCase(fetchClients.fulfilled, (state, action) => {
        state.clients = action.payload.map<Client>((c) => ({
          ...c,
          risk_history: c.risk_history ?? [],
          sentiment: sentiment_status(c),
          last_callback: c.risk_history ? c.risk_history[0]?.last_callback : null,
          last_mood: c.risk_history ? c.risk_history[0]?.mood : null,
        }));
        state.loadingClients = false;
      })
      .addCase(fetchClients.rejected, (state, action) => {
        if (action.error.message !== 'Aborted') {
          state.loadingClients = false;
        }
      });
  },
});

export const { handleTableChange, handleFiltersChange, clearTableFilters, clearCaseStatusFilters } =
  dashboardSlice.actions;
